import { useQueryString } from "@coideo/barrani";
import { usePos } from "context/auth-context";
import { DateTime } from "luxon";
import { Event, type EventJson } from "models/event";
import { Show, type ShowJson } from "models/show";
import { type Venue } from "models/venue";
import { useInfiniteQuery, useQuery } from "react-query";
import { client } from "utils/api-client";
import { DATE_MEDIUM } from "utils/dates";
import { useParam } from "utils/use-params";

type EventFilters = Partial<{ search: string; venue: string; dateTo: string }>;

const eventKeys = {
  all: (basePath: string) => [{ scope: "events", basePath }] as const,
  list: (basePath: string, filters: EventFilters) =>
    [{ ...eventKeys.all(basePath)[0], filters }] as const,
  detail: (basePath: string, eventId: string) =>
    [{ ...eventKeys.all(basePath)[0], eventId }] as const,
  months: (basePath: string, eventId: string, year: string, month: string) =>
    [{ ...eventKeys.detail(basePath, eventId)[0], type: "months", year, month }] as const,
  hours: (basePath: string, eventId: string, day: string | null) =>
    [{ ...eventKeys.detail(basePath, eventId)[0], type: "hours", day }] as const,
  shows: (basePath: string, eventId: string, showId: number | undefined) =>
    [{ ...eventKeys.detail(basePath, eventId)[0], type: "shows", showId }] as const,
};

export type Channel = {
  id: number;
  name: string;
};

type ComboListItem = {
  id: number;
  name: string;
  isNumbered: boolean;
  color: string;
  uuid: string;
};

type ShowListItemJson = {
  id: number;
  name: string;
  startDate: string;
  endDate: string;
  doorTime: string;
  salesStart: null;
};

class ShowListItem {
  readonly id: number;
  readonly name: string;
  readonly startDate: DateTime;

  static fromJson = (json: ShowListItemJson) =>
    new ShowListItem(json.id, json.name, DateTime.fromISO(json.startDate));

  constructor(id: number, name: string, startDate: DateTime) {
    this.id = id;
    this.name = name;
    this.startDate = startDate;
  }

  getDescription() {
    const startDate = this.startDate.toLocaleString(DATE_MEDIUM);

    return this.name ? `${this.name} (${startDate})` : startDate;
  }
}

type EventListItemJson = {
  id: number;
  name: string;
  description: string;
  thumbImage: string;
  image: string;
  currency: string;
  venue: Venue;
  shows: ShowListItemJson[];
  combos?: ComboListItem[];
};

export class EventListItem {
  readonly id: number;
  readonly name: string;
  readonly thumbImage: string;
  readonly venue: Venue;
  readonly shows: readonly ShowListItem[];
  private readonly combos?: readonly ComboListItem[];

  static fromJson(json: EventListItemJson) {
    return new EventListItem(
      json.id,
      json.name,
      json.thumbImage,
      json.venue,
      json.shows.map(ShowListItem.fromJson),
      json.combos,
    );
  }

  constructor(
    id: number,
    name: string,
    thumbImage: string,
    venue: Venue,
    shows: ShowListItem[],
    combos?: ComboListItem[],
  ) {
    this.id = id;
    this.name = name;
    this.thumbImage = thumbImage;
    this.venue = venue;
    this.shows = shows;
    this.combos = combos;
  }

  hasCombos() {
    return this.combos && this.combos.length > 0;
  }
}

const MAX = 25;

function useEvents() {
  const { basePath } = usePos();
  const [queryString, search] = useQueryString<EventFilters>({ search: "" });

  const result = useInfiniteQuery(
    eventKeys.list(basePath, queryString),
    async ({ signal, pageParam }) =>
      await client<EventListItemJson[]>(`${basePath}/events`, {
        queryString: { ...queryString, max: MAX.toString(), offset: pageParam || "0" },
        signal,
      }).then((events) => events.map(EventListItem.fromJson)),
    {
      getNextPageParam: (lastPage, pages) =>
        lastPage.length < MAX ? undefined : pages.reduce((acc, page) => acc + page.length, 0),
    },
  );

  return { ...result, queryString, search };
}

function useEventId() {
  return useParam("eventId");
}

function useEvent(
  options: { refetchOnWindowFocus?: boolean; onSuccess?: (data: Event) => void } = {},
) {
  const { basePath } = usePos();
  const eventId = useEventId();

  return useQuery(
    eventKeys.detail(basePath, eventId),
    async ({ signal }) =>
      await client<EventJson>(`${basePath}/events/${eventId}`, { signal }).then(Event.fromJson),
    options,
  );
}

type ShowDay = { available: boolean; day: string };

function useCalendarDays(year: string, month: string) {
  const { basePath } = usePos();
  const eventId = useEventId();

  return useQuery(
    eventKeys.months(basePath, eventId, year, month),
    async ({ signal }) =>
      await client<{ days: ShowDay[] }>(
        `${basePath}/events/${eventId}/shows/months/${year}/${month}`,
        { signal },
      ).then(({ days }) => days.sort((a, b) => (a.day > b.day ? 1 : -1))),
    {
      enabled: Boolean(month),
      refetchOnWindowFocus: false,
    },
  );
}

export type CalendarHour = {
  id: number;
  name: null;
  startDate: string;
  endDate: string;
  isAvailable: boolean;
};

function useCalendarHours(day: string | null) {
  const { basePath } = usePos();
  const eventId = useEventId();

  return useQuery(
    eventKeys.hours(basePath, eventId, day),
    async ({ signal }) =>
      await client<{ shows: CalendarHour[] }>(
        `${basePath}/events/${eventId}/shows/days/${day ?? ""}`,
        { signal },
      ).then(({ shows }) => shows.sort((a, b) => (a.startDate > b.startDate ? 1 : -1))),
    {
      enabled: Boolean(day),
      refetchOnWindowFocus: false,
    },
  );
}

function useCalendarShows(
  event: Event,
  showId: number | undefined,
  onSuccess: (data: Show | null) => void,
) {
  const { basePath } = usePos();
  const eventId = useEventId();

  return useQuery(
    eventKeys.shows(basePath, eventId, showId),
    async () =>
      showId
        ? await client<ShowJson>(`${basePath}/events/${eventId}/shows/${showId}`).then((show) =>
            Show.fromJson(show, event.hasNoServiceCharge()),
          )
        : null,
    {
      onSuccess,
      refetchOnWindowFocus: false,
    },
  );
}

function useChannels() {
  const { basePath } = usePos();
  const eventId = useEventId();

  const [queryString] = useQueryString({
    type: "WEB",
  });

  return useQuery(
    ["venues", queryString],
    async ({ signal }) =>
      await client<Channel[]>(`${basePath}/events/${eventId}/channels`, {
        queryString,
        signal,
      }),
  );
}

export {
  useEvents,
  useEvent,
  useEventId,
  useCalendarDays,
  useCalendarHours,
  useCalendarShows,
  useChannels,
};
